import React, { useMemo } from 'react';
import {
  createSeededRng,
  getLetterAnimationsForVariant,
  hashString,
  type LetterAnimationClass,
  type LetterAnimationVariant,
} from '../../../constants/drunkVisionAnimations';

interface DrunkQuestionTextProps {
  text: string;
  className?: string;
  /** Approximate fraction of letters that get an animation (0–1). Default 0.45 so more letters animate */
  animateFraction?: number;
  /**
   * `drunk` – rotate / jump / tilt (Are You Drunk).
   * `vision` – size + spacing only (Vision Check).
   */
  variant?: LetterAnimationVariant;
}

interface LetterPlan {
  char: string;
  animation: LetterAnimationClass | null;
  delay: number;
}

/**
 * Build a per-question letter animation plan.
 * Shuffle which indices animate from a question-level seed so consecutive questions
 * don't always light up the same relative letter positions.
 */
function planLetterAnimations(
  text: string,
  animateFraction: number,
  variant: LetterAnimationVariant
): LetterPlan[] {
  const pool = getLetterAnimationsForVariant(variant);
  const rng = createSeededRng(hashString(`${variant}::${text}`));

  const letterIndices: number[] = [];
  for (let i = 0; i < text.length; i++) {
    if (!/\s/.test(text[i])) letterIndices.push(i);
  }

  // Fisher–Yates shuffle of eligible letter indices
  for (let i = letterIndices.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = letterIndices[i];
    letterIndices[i] = letterIndices[j];
    letterIndices[j] = tmp;
  }

  const animateCount = Math.max(
    0,
    Math.min(letterIndices.length, Math.round(letterIndices.length * animateFraction))
  );
  const animated = new Map<number, { animation: LetterAnimationClass; delay: number }>();

  for (let k = 0; k < animateCount; k++) {
    const index = letterIndices[k];
    const animation = pool[Math.floor(rng() * pool.length)];
    // Keep stagger modest (0–0.7s) so rest windows overlap and text stays readable
    const delay = Math.round(rng() * 7) / 10;
    animated.set(index, { animation, delay });
  }

  const plan: LetterPlan[] = [];
  for (let i = 0; i < text.length; i++) {
    const entry = animated.get(i);
    plan.push({
      char: text[i],
      animation: entry?.animation ?? null,
      delay: entry?.delay ?? 0,
    });
  }
  return plan;
}

export const DrunkQuestionText: React.FC<DrunkQuestionTextProps> = ({
  text,
  className = '',
  animateFraction = 0.45,
  variant = 'drunk',
}) => {
  const letters = useMemo(
    () => planLetterAnimations(text, animateFraction, variant),
    [text, animateFraction, variant]
  );

  const textClass =
    variant === 'vision'
      ? `${className} drunk-question-text vision-question-text`
      : `${className} drunk-question-text`;

  return (
    <h1 className={textClass}>
      {letters.map(({ char, animation, delay }, i) =>
        animation ? (
          <span
            key={i}
            className={`drunk-letter ${animation}`}
            style={{ animationDelay: `${delay}s` }}
          >
            {char}
          </span>
        ) : (
          <span key={i}>{char}</span>
        )
      )}
    </h1>
  );
};
